import { Dispatch, ReactElement, SetStateAction, createContext, useContext, useEffect, useState } from 'react';
import { getNewMessages } from '@/generated/client/messages/messages';
import { ThreadUserThreadId } from '@/types';
import { ctxUser } from './UserContext';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { DetailedMessage } from '@/generated/model';

export type MessagesContextType = {
  sentMessage: DetailedMessage | undefined;
  setSentMessage: Dispatch<SetStateAction<DetailedMessage | undefined>>;

  thread: ThreadUserThreadId | undefined;
  setThread: Dispatch<SetStateAction<ThreadUserThreadId | undefined>>;
  newMessages: Map<number, DetailedMessage[]>;

  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ctxMessages = createContext<MessagesContextType | undefined>(undefined);

export function MessagesContext({ children }: { children: ReactElement }) {
  const [sentMessage, setSentMessage] = useState<DetailedMessage | undefined>(undefined);
  const [newMessages, setNewMessages] = useState<Map<number, DetailedMessage[]>>(new Map());
  const [thread, setThread] = useState<ThreadUserThreadId | undefined>(undefined);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const userCtx = useContext(ctxUser);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined = undefined;

    function setTimeoutForPoll(fromTime: number) {
      timer = setTimeout(() => poll(fromTime), 60000);
    }

    async function poll(fromTime: number) {
      try {
        const result = await getNewMessages({
          asynchronous: false,
          fromTime,
        });

        // FIXME result.data.messages nem lehetne null
        if (result.status === 200 && result.data?.messages?.length) {
          const receivedMessages = result.data.messages;
          if (receivedMessages && receivedMessages.length) {
            const newMap = new Map<number, DetailedMessage[]>();
            receivedMessages.forEach((message) => {
              if (newMap.has(message.threadId)) {
                newMap.get(message.threadId)?.push(message);
              } else {
                newMap.set(message.threadId, [message]);
              }
            });
            setNewMessages(newMap);
          }
          setTimeoutForPoll(result.data.serverTimeMicros);
        } else {
          setTimeoutForPoll(fromTime);
        }
      } catch (error) {
        setTimeoutForPoll(fromTime);
        rethrowIfNotClientException(error);
      }
    }

    if (userCtx?.isToken()) return;

    void poll(-1);

    return () => {
      clearTimeout(timer);
    };
  }, [userCtx]);

  return (
    <ctxMessages.Provider
      value={{
        sentMessage,
        setSentMessage,
        thread,
        setThread,
        newMessages,
        searchText,
        setSearchText,
      }}
    >
      {children}
    </ctxMessages.Provider>
  );
}
